import { useCallback, useRef, PointerEvent, useMemo } from 'react';
import { MotionValue } from 'framer-motion';
import { GridLayout } from './use-grid-layout';
import { useScrollPosition } from './use-scroll-position';
import { useStore } from '@/store';
import { calculateGridIndex, calculateScrollPosition, calculateResistance } from './helpers';
import { useIsRightToLeft } from '@/utils/use-is-rtl';

export function useDrag<T extends HTMLElement>(
  gridLayout: GridLayout,
  root: T | null
): {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: () => void;
  scrollX: MotionValue;
  scrollY: MotionValue;
  scrollBase: { x: number; y: number };
} {
  const { x: scrollX, y: scrollY, scrollBase } = useScrollPosition(gridLayout);
  const drag = useRef({ isDragging: false, x: 0, y: 0, initialized: false });
  const prefersReducedMotion = useStore((state) => state.prefersReducedMotion);
  const canPan = scrollBase.x || scrollBase.y;
  const isRtl = useIsRightToLeft();
  const mod = useMemo(() => (isRtl ? -1 : 1), [isRtl]);

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      e.preventDefault();
      drag.current.x = e.clientX;
      drag.current.y = e.clientY;
      drag.current.isDragging = true;
      drag.current.initialized = false;

      if (root) {
        root.style.cursor = 'grabbing';
      }
    },
    [root]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!canPan) {
        return;
      }

      if (drag.current.isDragging || (!drag.current.isDragging && !prefersReducedMotion)) {
        const { containerWidth, containerHeight, cardWidth, cardHeight, gridWidth, gridHeight } =
          gridLayout;

        // update previous and current positions
        const prevX = drag.current.x;
        const prevY = drag.current.y;
        drag.current.x = e.clientX;
        drag.current.y = e.clientY;

        if (!drag.current.initialized) {
          drag.current.initialized = true;
          return;
        }

        // calculate current delta
        const deltaX = mod * (drag.current.x - prevX) * (drag.current.isDragging ? 1 : -1);
        const deltaY = (drag.current.y - prevY) * (drag.current.isDragging ? 1 : -1);

        // calculate resistance if we're at edges
        const dragResistanceX = calculateResistance(
          scrollX.get() - (containerWidth - cardWidth) * 0.5,
          0,
          cardWidth - gridWidth,
          Math.sign(deltaX)
        );

        const dragResistanceY = calculateResistance(
          scrollY.get() - (containerHeight - cardHeight) * 0.5,
          0,
          cardHeight - gridHeight,
          Math.sign(deltaY)
        );

        // update scroll offset
        const multiplier = drag.current.isDragging ? 2 : 0.15;

        scrollX.set(scrollX.get() + deltaX * multiplier * dragResistanceX);
        scrollY.set(scrollY.get() + deltaY * multiplier * dragResistanceY);
      }
    },
    [canPan, prefersReducedMotion, gridLayout, scrollX, scrollY, mod]
  );

  const onPointerUp = useCallback(() => {
    drag.current.isDragging = false;

    if (root) {
      root.style.cursor = 'grab';
    }

    const index = calculateGridIndex(scrollX.get(), scrollY.get(), gridLayout);
    const [x, y] = calculateScrollPosition(index, gridLayout);

    scrollX.set(x);
    scrollY.set(y);
  }, [gridLayout, scrollX, scrollY, root]);

  return { onPointerDown, onPointerMove, onPointerUp, scrollX, scrollY, scrollBase };
}
